import { useState } from 'react';
import './discover.css';
import cafeImg from '../../assets/cafe.png';

function ArrowLeftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M19 12H5M11 6l-6 6 6 6" stroke="#3d2f7a" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
      <path d="M6 6l12 12M18 6L6 18" stroke="#3d2f7a" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <path d="M5 13l4.5 4.5L19 8" stroke="#ffffff" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function NavigationIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M12 2 4 21l8-4.5L20 21 12 2Z" stroke="#6c4ce8" strokeWidth="1.8" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

const TOTAL_STEPS = 3;

// The best-available human-readable location text for the selected shop, in
// the order Admin's Shop fields most reliably describe where it actually
// is. Returns null (never a fabricated address) when selectedShop or all
// three fields are missing, so callers can tell "no real data" apart from
// an actual (possibly short) address/description string. latitude/longitude
// are deliberately never surfaced as visible text here.
function getShopLocationText(selectedShop) {
  if (!selectedShop) return null;
  return selectedShop.pinned_address || selectedShop.address || selectedShop.description || null;
}

// How close (in metres) the user's GPS fix must be to the shop's pinned
// coordinates for "I Found It!" to count. Phone GPS is often off by
// 20-50 m, so the fix's own reported accuracy is added on top (capped so a
// very poor fix can't unlock the step from far away).
const FOUND_RADIUS_METERS = 100;
const MAX_ACCURACY_SLACK_METERS = 100;

// Returns { lat, lng } for the shop, or null when Admin hasn't pinned it.
// Laravel may send decimals as strings, so they are parsed here.
function getShopCoords(selectedShop) {
  if (!selectedShop) return null;
  const lat = parseFloat(selectedShop.latitude);
  const lng = parseFloat(selectedShop.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

// Great-circle distance between two points, in metres (haversine).
function distanceInMeters(a, b) {
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

function getCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('unsupported'));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    });
  });
}

function formatDistance(meters) {
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;
}

function formatCoords({ lat, lng }) {
  return `${lat.toFixed(6)}, ${lng.toFixed(6)}`;
}

export default function Discover({
  onBack,
  onFound,
  cafeName = 'Brew House',
  selectedShop = null,
}) {
  const locationText = getShopLocationText(selectedShop);
  const locationDisplay = locationText || 'Location unavailable';
  const [checking, setChecking] = useState(false);
  // null when hidden, otherwise { title, message, here?, shop? } for the
  // "not here yet" popup. here/shop are { lat, lng } shown under the message.
  const [popup, setPopup] = useState(null);

  const openDirections = () => {
    // Only real Admin-provided data ever reaches the maps query — the
    // "Location unavailable" placeholder is a display-only fallback and is
    // never sent as if it were an address.
    const destination = encodeURIComponent([cafeName, locationText].filter(Boolean).join(' '));
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleFound = async () => {
    if (checking) return;

    setChecking(true);
    try {
      const position = await getCurrentPosition();
      const here = { lat: position.coords.latitude, lng: position.coords.longitude };
      const shopCoords = getShopCoords(selectedShop);

      // If Admin hasn't pinned this shop, we still need the user's live GPS for
      // the backend Step 1 validation. In that case, the user's current location
      // is the authoritative signal we have to submit.
      if (!shopCoords) {
        onFound?.(here);
        return;
      }

      const distance = distanceInMeters(here, shopCoords);
      const slack = Math.min(position.coords.accuracy || 0, MAX_ACCURACY_SLACK_METERS);

      if (distance <= FOUND_RADIUS_METERS + slack) {
        onFound?.(here);
        return;
      }
      setPopup({
        title: "You're not there yet",
        message: `You're about ${formatDistance(distance)} away from ${cafeName}. Go to the shop location and tap "I Found It!" again.`,
        here,
        shop: shopCoords,
      });
    } catch (err) {
      const denied = err && err.code === 1;
      setPopup({
        title: denied ? 'Location access needed' : "Couldn't get your location",
        message: denied
          ? `Allow location access so we can check you're at ${cafeName}, then try again.`
          : `We couldn't read your current location. Go to ${cafeName} and try again.`,
      });
    } finally {
      setChecking(false);
    }
  };

  const goToShop = () => {
    setPopup(null);
    openDirections();
  };

  return (
    <div className="discover-screen">
      <div className="discover-frame">
        <div className="discover-topbar">
          <button type="button" className="discover-back-btn" aria-label="Go back" onClick={onBack}>
            <ArrowLeftIcon />
          </button>

          <div className="discover-stepper">
            {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((n) => (
              <span
                key={n}
                className={'discover-step-dot' + (n === 1 ? ' discover-step-dot-active' : '')}
              >
                {n}
              </span>
            ))}
          </div>

          <span className="discover-topbar-spacer" />
        </div>

        <div className="discover-hero">
          <span className="discover-badge">1</span>
          <div className="discover-hero-text">
            <div className="discover-title-row">
              <h1 className="discover-title">{cafeName}</h1>
            </div>
          </div>
        </div>

        <div className="discover-image-card">
          <img src={cafeImg} alt="Cafe storefront" className="discover-image" />

          <button
            type="button"
            className="discover-corner-pill-btn"
            aria-label="Get directions"
            onClick={openDirections}
          >
            <NavigationIcon />
            <span>Take me to the shop</span>
          </button>

          <div className="discover-image-overlay">
            <div className="discover-location">
              <span className="discover-location-name">Hint: {locationDisplay}</span>
            </div>
          </div>
        </div>

        <div className="discover-cta-wrap">
          <button
            type="button"
            className="discover-cta"
            onClick={handleFound}
            disabled={checking}
            aria-label="I Found It!"
            aria-busy={checking}
          >
            <span className="discover-cta-text">{checking ? 'Checking…' : 'I Found It!'}</span>
          </button>
        </div>

        {popup && (
          <div className="discover-modal-backdrop" onClick={() => setPopup(null)}>
            <div
              className="discover-modal-card"
              role="dialog"
              aria-modal="true"
              aria-labelledby="discover-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="discover-modal-close-btn"
                aria-label="Close"
                onClick={() => setPopup(null)}
              >
                <CloseIcon />
              </button>
              <h2 id="discover-modal-title" className="discover-modal-title">{popup.title}</h2>
              <p className="discover-modal-message">{popup.message}</p>
              {popup.here && popup.shop && (
                <div className="discover-modal-coords">
                  <div>
                    <span className="discover-modal-coords-label">You:</span> {formatCoords(popup.here)}
                  </div>
                  <div>
                    <span className="discover-modal-coords-label">Shop:</span> {formatCoords(popup.shop)}
                  </div>
                </div>
              )}
              <button type="button" className="discover-modal-primary" onClick={goToShop}>
                <NavigationIcon />
                <span>Go to the shop location</span>
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
